import React, { useEffect, useState } from 'react';
import axios from 'axios';

interface Customer {
  id: string;
  name: string;
  mapLat: number;
  mapLng: number;
  mapZoom: number;
  createdAt: string;
  _count: { users: number; projects: number };
}

const Kunden: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editCustomer, setEditCustomer] = useState<Customer | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [name, setName] = useState('');
  const [mapLat, setMapLat] = useState('');
  const [mapLng, setMapLng] = useState('');
  const [mapZoom, setMapZoom] = useState('13');
  const [geocoding, setGeocoding] = useState(false);
  const [geoQuery, setGeoQuery] = useState('');

  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canDelete = ['SUPER_ADMIN', 'ADMIN'].includes(user.role);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchCustomers(); }, []); // eslint-disable-line

  const resetForm = () => {
    setName(''); setMapLat(''); setMapLng(''); setMapZoom('13'); setGeoQuery('');
  };

  const openCreate = () => {
    setEditCustomer(null);
    resetForm();
    setShowForm(true);
  };

  const openEdit = (c: Customer) => {
    setEditCustomer(c);
    setName(c.name);
    setMapLat(c.mapLat ? String(c.mapLat) : '');
    setMapLng(c.mapLng ? String(c.mapLng) : '');
    setMapZoom(c.mapZoom ? String(c.mapZoom) : '13');
    setGeoQuery('');
    setShowForm(true);
  };

  const geocodeCity = async () => {
    if (!geoQuery) return;
    setGeocoding(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(geoQuery + ', Germany')}&limit=1`,
        { headers: { 'Accept-Language': 'de' } }
      );
      const data = await res.json();
      if (data.length > 0) {
        setMapLat(parseFloat(data[0].lat).toFixed(6));
        setMapLng(parseFloat(data[0].lon).toFixed(6));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setGeocoding(false);
    }
  };

  const handleSave = async () => {
    if (!name) return;
    setLoading(true);
    try {
      if (editCustomer) {
        await axios.patch(`${process.env.REACT_APP_API_URL}/api/customers/${editCustomer.id}`, {
          name,
          mapLat: mapLat ? parseFloat(mapLat) : null,
          mapLng: mapLng ? parseFloat(mapLng) : null,
          mapZoom: mapZoom ? parseInt(mapZoom) : 13
        }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/customers`, {
          name,
          mapLat: mapLat ? parseFloat(mapLat) : null,
          mapLng: mapLng ? parseFloat(mapLng) : null,
          mapZoom: mapZoom ? parseInt(mapZoom) : 13
        }, { headers: { Authorization: `Bearer ${token}` } });
      }
      setShowForm(false);
      setEditCustomer(null);
      resetForm();
      fetchCustomers();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/customers/${deleteConfirm.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteConfirm(null);
      fetchCustomers();
    } catch (err) {
      console.error(err);
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={s.container}>
      <div style={s.sidebar}>
        <div style={{ padding: '0 16px 24px 16px', textAlign: 'center' }}>
          <img src="/portalis_logo.png" alt="Portalis" style={{ width: '160px' }} />
        </div>
        <nav style={s.nav}>
          <a href="/dashboard" style={s.navItem}>Startseite</a>
          <a href="/projects" style={s.navItem}>Liegenschaften</a>
          <a href="/users" style={s.navItem}>Benutzer</a>
          <a style={s.navItemActive}>Kunden</a>
        </nav>
        <div style={s.userInfo}>
          <p style={s.userName}>{user.firstName} {user.lastName}</p>
          <p style={s.userRole}>{user.role}</p>
          <button style={s.logoutButton} onClick={() => { localStorage.clear(); window.location.href = '/login'; }}>
            Abmelden
          </button>
        </div>
      </div>

      <div style={s.main}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>Kunden</h1>
            <p style={s.subtitle}>{customers.length} Kunden</p>
          </div>
          <button style={s.addButton} onClick={openCreate}>+ Neuer Kunde</button>
        </div>

        <input
          style={{ ...s.input, marginBottom: '16px', width: '100%', boxSizing: 'border-box' }}
          placeholder="🔍 Kundenname suchen..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />

        {showForm && (
          <div style={s.form}>
            <h3 style={s.formTitle}>{editCustomer ? 'Kunde bearbeiten' : 'Neuen Kunden anlegen'}</h3>
            <div style={s.formGrid}>
              <div style={{ ...s.inputGroup, gridColumn: '1 / -1' }}>
                <label style={s.label}>Name *</label>
                <input style={s.input} value={name} onChange={e => setName(e.target.value)} placeholder="z.B. Stadt Duisburg" />
              </div>
              <div style={{ ...s.inputGroup, gridColumn: '1 / -1' }}>
                <label style={s.label}>Kartenstandard – Stadt suchen</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input style={{ ...s.input, flex: 1 }} value={geoQuery} onChange={e => setGeoQuery(e.target.value)} placeholder="z.B. Duisburg" onKeyDown={e => e.key === 'Enter' && geocodeCity()} />
                  <button style={s.geoBtn} onClick={geocodeCity} disabled={geocoding}>
                    {geocoding ? '...' : '📍 Suchen'}
                  </button>
                </div>
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Latitude</label>
                <input style={s.input} value={mapLat} onChange={e => setMapLat(e.target.value)} placeholder="z.B. 51.4344" />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Longitude</label>
                <input style={s.input} value={mapLng} onChange={e => setMapLng(e.target.value)} placeholder="z.B. 6.7623" />
              </div>
              <div style={s.inputGroup}>
                <label style={s.label}>Zoom (1-20)</label>
                <input style={s.input} type="number" min="1" max="20" value={mapZoom} onChange={e => setMapZoom(e.target.value)} />
              </div>
            </div>
            <div style={s.formButtons}>
              <button style={s.cancelButton} onClick={() => { setShowForm(false); setEditCustomer(null); resetForm(); }}>Abbrechen</button>
              <button style={{ ...s.saveButton, opacity: loading ? 0.7 : 1 }} onClick={handleSave} disabled={loading}>
                {loading ? 'Speichern...' : 'Speichern'}
              </button>
            </div>
          </div>
        )}

        <div style={s.grid}>
          {filteredCustomers.map(c => (
            <div key={c.id} style={s.card}>
              <div style={s.cardHeader}>
                <h3 style={s.cardTitle}>{c.name}</h3>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button style={s.iconBtn} onClick={() => openEdit(c)} title="Bearbeiten">✏️</button>
                  {canDelete && (
                    <button style={s.iconBtn} onClick={() => setDeleteConfirm(c)} title="Löschen">🗑️</button>
                  )}
                </div>
              </div>
              <div style={s.cardStats}>
                <span style={s.stat}>👥 {c._count?.users || 0} Benutzer</span>
                <span style={s.stat}>🏢 {c._count?.projects || 0} Liegenschaften</span>
              </div>
              {c.mapLat && c.mapLng && (
                <p style={s.coords}>📍 {Number(c.mapLat).toFixed(4)}, {Number(c.mapLng).toFixed(4)} · Zoom {c.mapZoom}</p>
              )}
              <p style={s.date}>Erstellt: {new Date(c.createdAt).toLocaleDateString('de-DE')}</p>
            </div>
          ))}
        </div>
      </div>

      {deleteConfirm && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <p style={{ fontSize: '32px', margin: '0 0 8px 0' }}>🗑️</p>
            <p style={{ fontSize: '18px', fontWeight: '600', color: '#0f2744', margin: '0 0 8px 0' }}>Kunde löschen</p>
            <p style={{ color: '#666', fontSize: '14px', margin: '0 0 24px 0' }}>
              Möchtest du <strong>{deleteConfirm.name}</strong> wirklich löschen? Alle zugehörigen Benutzer und Liegenschaften werden ebenfalls gelöscht.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={() => setDeleteConfirm(null)} style={s.cancelButton}>Abbrechen</button>
              <button onClick={handleDelete} style={s.deleteButton}>Löschen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', height: '100vh', backgroundColor: '#f5f5f5' },
  sidebar: { width: '220px', backgroundColor: '#0f2744', display: 'flex', flexDirection: 'column', padding: '24px 0', flexShrink: 0 },
  logo: { color: 'white', textAlign: 'center', letterSpacing: '3px', fontSize: '18px', margin: '0 0 32px 0', padding: '0 24px' },
  nav: { display: 'flex', flexDirection: 'column', flex: 1 },
  navItem: { color: '#aac4e0', padding: '12px 24px', cursor: 'pointer', fontSize: '14px', textDecoration: 'none', display: 'block' },
  navItemActive: { color: 'white', padding: '12px 24px', fontSize: '14px', backgroundColor: '#1e3a5f', borderLeft: '3px solid #00c49a', display: 'block' },
  userInfo: { padding: '24px', borderTop: '1px solid #1e3a5f' },
  userName: { color: 'white', margin: '0', fontSize: '14px', fontWeight: '600' },
  userRole: { color: '#00c49a', margin: '4px 0 12px 0', fontSize: '12px' },
  logoutButton: { backgroundColor: 'transparent', border: '1px solid #aac4e0', color: '#aac4e0', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', width: '100%' },
  main: { flex: 1, padding: '32px', overflow: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { fontSize: '28px', color: '#0f2744', margin: '0' },
  subtitle: { color: '#888', margin: '4px 0 0 0' },
  addButton: { backgroundColor: '#00c49a', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 18px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  form: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  formTitle: { margin: '0 0 20px 0', color: '#0f2744' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#333' },
  input: { padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', fontFamily: 'inherit' },
  geoBtn: { padding: '10px 16px', borderRadius: '8px', border: 'none', backgroundColor: '#0f2744', color: 'white', cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' as const },
  formButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '16px' },
  cancelButton: { backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '8px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  saveButton: { backgroundColor: '#00c49a', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  deleteButton: { backgroundColor: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' },
  card: { backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderTop: '4px solid #3498db' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' },
  cardTitle: { margin: '0', color: '#0f2744', fontSize: '16px' },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px', padding: '2px 4px' },
  cardStats: { display: 'flex', gap: '16px', marginBottom: '8px' },
  stat: { color: '#666', fontSize: '13px' },
  coords: { color: '#888', fontSize: '12px', margin: '0 0 4px 0' },
  date: { color: '#aaa', fontSize: '12px', margin: '0' },
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '32px', maxWidth: '420px', width: '90%', textAlign: 'center' },
};

export default Kunden;



